/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { LogService, UtilsService } from '../../../../../../core/services';
import { NamespaceService } from '../../../../shared/services/utils';
import { SecurityApiService } from '../../../../shared/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-certificates-details',
    templateUrl: './certificates-details.component.html',
    styleUrls: ['./certificates-details.component.scss']
})
export class CertificatesDetailsComponent extends BaseComponent {
    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Store widget data for the view.
     */
    public widgetData = {
        total: 0,
        expiring: 0,
        expired: 0
    };

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Store the search term.
     */
    public searchTerm: string = '';

    constructor(
        private activatedRoute: ActivatedRoute,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private securityApiService: SecurityApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        if (id) {
            this.setUpPageData(id);
        } else {
            this.logService.error('CertificatesDetailsComponent:constructor', 'No ID in url');
        }
    }

    /**
     * Update search.
     * @param $event
     */
    public updateSearch($event: any): void {
        this.searchTerm = $event;
    }

    /**
     * Filter the table data by widget clicked.
     * @param type
     */
    public filterDataViaWidget(type: string): void {
        if (this.searchTerm.includes(type)) {
            return;
        } else {
            if (this.searchTerm !== '') {
                this.searchTerm += ',' + type;
            } else {
                this.searchTerm = type;
            }
        }
    }

    /**
     * Clear search
     */
    public clearSearch(): void {
        this.searchTerm = '';
    }

    /**
     * Set up page data.
     * @param id
     * @private
     */
    private setUpPageData(id: string): void {
        this.securityApiService.getCertificatesByAdc(this.selectedNamespaceKey, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: data => {
                        if (data?.length) {
                            this.populateWidgetDetails(data);
                            this.populateTableRows(data);
                        }
                    },
                    error: error => {
                        this.logService.error('SecurityDetailsComponent:setUpPageData', 'Unable to get page data. ' + error);

                    }
                }
            );
    }

    /**
     * Populate widget data.
     * @param data
     * @private
     */
    private populateWidgetDetails(data: any): void {
        const widgetCounts = {
            expiring: [],
            expired: []
        };
        for (const item of data) {
            const warning = this.getWarning(item.notAfter);
            if (warning === 'expired') {
                widgetCounts.expired.push(warning);
            }

            if (warning === 'expiring') {
                widgetCounts.expiring.push(warning);
            }
        }
        this.widgetData = {
            total: data.length,
            expiring: widgetCounts.expiring.length || 0,
            expired: widgetCounts.expired.length || 0
        };
    }

    /**
     * Populate table rows.
     * @param data
     * @private
     */
    private populateTableRows(data: any): void {
        this.tableRows = [];
        for (const item of data) {
            this.tableRows.push(
                {
                    certificateName: item.subject,
                    commonName: this.formatCommonNamesArray(item.dnsNames),
                    expiryDate: this.utilsService.createDateTimeString(item.notAfter),
                    issuer: item.issuer,
                    startingDate: this.utilsService.createDateTimeString(item.notBefore),
                    warning: this.getWarning(item.notAfter)
                }
            );
        }
    }

    /**
     * Format common names array.
     * @param dnsNames
     * @private
     */
    private formatCommonNamesArray(dnsNames: string[]): string {
        const commonNamesString = dnsNames?.toString();

        if (commonNamesString) {
            return commonNamesString.replace(/,/g, ', ');
        }
    }

    /**
     * Get warning message.
     * @param expiryDate
     * @private
     */
    private getWarning(expiryDate: string): string {
        const today: Date = new Date();

        const dateExpiry: Date = new Date(expiryDate);
        if (today > dateExpiry) {
            return 'expired';
        }

        const plus30Days: Date = new Date(today.setDate(today.getDate() + 30));
        if (dateExpiry < plus30Days) {
            return 'expiring';
        } else {
            return 'valid';
        }
    }
}
